'use client';

import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, VStack } from '@terra/ui-styled-system';
import { MfaMessagesToggleHeader } from '../../mfa/MfaMessagesToggleHeader';
import { EmailMessageBody } from '../../mfa/EmailMessageBody';
import { EmailRawSourceViewer } from '../../mfa/EmailRawSourceViewer';
import { MetaDefault } from '../../../components/typography';
import { useTranslations } from 'next-intl';
import { useDateFormatter } from '@terra/ui-formatters';
import { useCredentialDataSources } from '../CredentialDataSourcesContext';

const CREDENTIAL_INBOX_MESSAGES_QUERY_KEY = 'credential-inbox-messages';

type CredentialInboxMessagesSectionProps = {
  applicationId: string;
  credentialId: string;
};

export const CredentialInboxMessagesSection = ({
  applicationId,
  credentialId,
}: CredentialInboxMessagesSectionProps) => {
  const tCard = useTranslations('designSystem.credentials.card');
  const tEmail = useTranslations('designSystem.mfa.emailDisplay');
  const dateFormatter = useDateFormatter();
  const { fetchCredentialInbox } = useCredentialDataSources();
  const [isOpen, setIsOpen] = useState(false);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: [
      CREDENTIAL_INBOX_MESSAGES_QUERY_KEY,
      applicationId,
      credentialId,
    ],
    queryFn: async () => {
      if (!fetchCredentialInbox) {
        throw new Error('Credential inbox is unavailable');
      }

      const result = await fetchCredentialInbox({ credentialId });

      if (!result.success) {
        throw new Error(result.error ?? 'Failed to load credential inbox');
      }

      return result;
    },
    enabled:
      isOpen &&
      Boolean(fetchCredentialInbox) &&
      Boolean(applicationId) &&
      Boolean(credentialId),
    staleTime: 0,
    gcTime: 0,
  });
  const messages = data?.messages ?? [];
  const loadFailed = Boolean(error);

  const handleToggle = useCallback(() => {
    setIsOpen((open) => !open);
  }, []);

  if (!fetchCredentialInbox) {
    return null;
  }

  return (
    <VStack alignItems="stretch" gap={2} py={2}>
      <MfaMessagesToggleHeader
        title={tCard('tabs.mfa.viewLatestEmail')}
        isExpanded={isOpen}
        onToggle={handleToggle}
        toggleLoading={isLoading && !isOpen}
        toggleAriaLabel={
          isOpen ? tEmail('hideMessages') : tEmail('showMessages')
        }
        refreshLabel={tEmail('refresh')}
        onRefresh={refetch}
        refreshLoading={isFetching}
      />
      {isOpen && !isLoading ? (
        <VStack alignItems="stretch" gap={2}>
          {loadFailed ? (
            <MetaDefault color="fg.neutral.error">
              {tEmail('failedToLoadMessages')}
            </MetaDefault>
          ) : null}
          {messages.length === 0 && !loadFailed ? (
            <MetaDefault color="fg.neutral.muted">
              {tEmail('noEmailMessages')}
            </MetaDefault>
          ) : (
            messages.map((msg) => (
              <Box
                key={msg.id}
                px={3}
                py={2}
                borderWidth="1px"
                borderColor="border.neutral.subtle"
                borderRadius="sm"
                bg="bg.neutral.subtle"
              >
                <VStack alignItems="stretch" gap={2}>
                  <EmailMessageBody content={msg.content} links={msg.links} />
                  <EmailRawSourceViewer
                    rawPlain={msg.rawPlain}
                    rawHtml={msg.rawHtml}
                  />
                  <MetaDefault color="fg.neutral.subtle">
                    {dateFormatter.format(new Date(msg.receivedAt))}
                    {msg.from ? ` — ${msg.from}` : ''}
                  </MetaDefault>
                </VStack>
              </Box>
            ))
          )}
        </VStack>
      ) : null}
      {isOpen && isLoading ? (
        <MetaDefault color="fg.neutral.muted">
          {tEmail('loadingMessages')}
        </MetaDefault>
      ) : null}
    </VStack>
  );
};
